import { Card, Statistic, Label, Icon, SemanticCOLORS } from "semantic-ui-react";
import { MetricTrend } from "../operations/queries/getPermitsCountByStatesMetric";

interface Props {
  label: string;
  value: number;
  previousValue?: number | null;
  deltaPercentage?: number | null;
  trend?: MetricTrend | null;
  /** Optional unit/suffix rendered after the value (e.g. "%", "days"). */
  unit?: string;
  /** Optional Semantic UI icon name shown next to the label. */
  icon?: string;
  /** Optional color accent for the card top border. */
  color?: SemanticCOLORS;
  /** When provided, the tile becomes clickable to drill down. */
  onClick?: () => void;
  /** Optional hint text under the value (e.g. "vs previous 30 days"). */
  comparisonHint?: string;
  /** When true, renders the tile in an active/focused visual state (drill-down selection). */
  selected?: boolean;
}

type TrendColor = "green" | "red" | "grey";

const trendVisuals: Record<MetricTrend, { color: TrendColor; icon: "arrow up" | "arrow down" | "minus" }> = {
  UP: { color: "green", icon: "arrow up" },
  DOWN: { color: "red", icon: "arrow down" },
  FLAT: { color: "grey", icon: "minus" },
};

const KpiTile = ({
  label,
  value,
  previousValue,
  deltaPercentage,
  trend,
  unit,
  icon,
  color,
  onClick,
  comparisonHint,
  selected,
}: Props) => {
  const hasComparison = previousValue !== undefined && previousValue !== null;
  const visuals = trend ? trendVisuals[trend] : trendVisuals.FLAT;

  const formatDelta = () => {
    if (deltaPercentage === undefined || deltaPercentage === null) {
      return null;
    }
    const sign = deltaPercentage > 0 ? "+" : "";
    return `${sign}${deltaPercentage}%`;
  };

  const deltaLabel = formatDelta();
  // Previous period exists but had no baseline value to compute a percentage from.
  const isNewActivity = hasComparison && (previousValue ?? 0) === 0 && value > 0;

  return (
    <Card as={onClick ? "a" : undefined} onClick={onClick} color={color} raised={selected}>
      <Card.Content textAlign="center">
        <Statistic size="small" color={color}>
          <Statistic.Label style={{ textTransform: "none" }}>
            {icon ? <Icon name={icon as any} /> : null}
            {label}
          </Statistic.Label>
          <Statistic.Value>
            {value.toLocaleString()}{" "}
            {unit ? <span style={{ fontSize: "0.5em", marginLeft: "0.25em" }}>{unit}</span> : null}
          </Statistic.Value>
        </Statistic>

        <div>
          {hasComparison && deltaLabel ? (
            <>
              <Label basic color={visuals.color} size="tiny">
                <Icon name={visuals.icon} />
                {deltaLabel}
              </Label>
              {hasComparison ? (
                <div style={{ fontSize: "0.78em", color: "#999", marginTop: "0.5em" }}>
                  {comparisonHint ?? "vs previous"} ({(previousValue as number).toLocaleString()})
                </div>
              ) : null}
            </>
          ) : null}
          {isNewActivity ? (
            <Label basic color="green" size="tiny">
              <Icon name="star outline" />
              New
            </Label>
          ) : null}
          {selected ? (
            <Label color="green" size="small">
              <Icon name="check" />
              Focused
            </Label>
          ) : null}
        </div>
      </Card.Content>
    </Card>
  );
};

export default KpiTile;
